import { z } from 'zod';
import { MoneySchema } from './api.js';

// Setter spec §06.10.5 — accumulated AI/telephony budget per prospect, configurable per project.
// Costs are ESTIMATES (§16: planning figures, not measured provider bills) unless the provider reported a cost.
// Unknown ≠ 0: usage without a rate is counted as «sin costo estimado», never as zero.
// The peso cap and alert threshold are pending with the owner: the setting ships disabled with no value («sin definir»).

const Id = z.string().min(1);
const Iso = z.string();
const MinorAmount = z.string().regex(/^\d{1,15}$/, 'Monto en centavos (entero)');

/** Meters that are spent on a prospect (LLM turns, voice minutes, telephony, WhatsApp). */
export const PROSPECT_COST_METERS = ['call_minutes', 'whatsapp_messages', 'whatsapp_templates', 'model_tokens_input', 'model_tokens_output', 'transcription_seconds'] as const;
export const ProspectCostMeterSchema = z.enum(PROSPECT_COST_METERS);
export type ProspectCostMeter = z.infer<typeof ProspectCostMeterSchema>;

/** How many meter units one rate applies to (rates are minor units — centavos — per this many units). */
export const PROSPECT_RATE_UNITS: Record<ProspectCostMeter, { per: number; label: string }> = {
  call_minutes: { per: 1, label: 'por minuto conectado (voz IA + telefonía)' },
  whatsapp_messages: { per: 1, label: 'por mensaje de sesión enviado' },
  whatsapp_templates: { per: 1, label: 'por plantilla enviada' },
  model_tokens_input: { per: 1_000_000, label: 'por millón de tokens de entrada' },
  model_tokens_output: { per: 1_000_000, label: 'por millón de tokens de salida' },
  transcription_seconds: { per: 60, label: 'por minuto de audio transcrito' },
};

/**
 * Reference rates from spec §16 (14 oct 2026, MXN, planning estimates — not quotes): §16.1 voice ≈ MXN 2.71 per
 * connected minute (already includes the voice LLM and Mexican mobile telephony); §16.2 Meta utility template and
 * service message ≈ MXN 0.1565. The spec gives no per-token or transcription rate: those stay unknown (null).
 */
export const PROSPECT_REFERENCE_RATES: Record<ProspectCostMeter, number | null> = {
  call_minutes: 271,
  whatsapp_messages: 15.65,
  whatsapp_templates: 15.65,
  model_tokens_input: null,
  model_tokens_output: null,
  transcription_seconds: null,
};

export const ProspectRatesSchema = z.object(
  Object.fromEntries(PROSPECT_COST_METERS.map((m) => [m, z.number().finite().min(0).max(1e9).nullable()])) as Record<ProspectCostMeter, z.ZodNullable<z.ZodNumber>>,
);
export type ProspectRates = z.infer<typeof ProspectRatesSchema>;

export const PutProspectBudgetConfigSchema = z
  .object({
    enabled: z.boolean(),
    /** Cap per prospect in this project, MXN centavos. null = sin definir. */
    capAmountMinor: MinorAmount.nullable(),
    /** Alert threshold (review progress signals from here). null = same as the cap. */
    alertAmountMinor: MinorAmount.nullable(),
    rates: ProspectRatesSchema,
  })
  .refine((b) => !b.enabled || (b.capAmountMinor !== null && BigInt(b.capAmountMinor) > 0n), { message: 'Define un monto máximo por prospecto para activar el presupuesto', path: ['capAmountMinor'] })
  .refine((b) => b.alertAmountMinor === null || b.capAmountMinor === null || BigInt(b.alertAmountMinor) <= BigInt(b.capAmountMinor), { message: 'El umbral de alerta debe ser menor o igual al monto máximo', path: ['alertAmountMinor'] });
export type PutProspectBudgetConfig = z.infer<typeof PutProspectBudgetConfigSchema>;

/** undefined = no peso value set (owner pending); disabled = value set but off; active = enforced. */
export const ProspectBudgetConfigStateSchema = z.enum(['undefined', 'disabled', 'active']);
export type ProspectBudgetConfigState = z.infer<typeof ProspectBudgetConfigStateSchema>;
export const PROSPECT_BUDGET_CONFIG_STATE_LABELS: Record<ProspectBudgetConfigState, string> = {
  undefined: 'Sin definir',
  disabled: 'Desactivado',
  active: 'Activo',
};

export const ProspectBudgetConfigSchema = z.object({
  projectId: Id,
  state: ProspectBudgetConfigStateSchema,
  enabled: z.boolean(),
  currency: z.string().length(3),
  cap: MoneySchema.nullable(),
  alertAt: MoneySchema.nullable(),
  rates: ProspectRatesSchema,
  referenceRates: ProspectRatesSchema,
  /** false = no row saved yet (reference rates in use). */
  customized: z.boolean(),
  updatedAt: Iso.nullable(),
});
export type ProspectBudgetConfig = z.infer<typeof ProspectBudgetConfigSchema>;

// ───────── Progress signals + reviews ─────────

export const ProspectProgressSignalsSchema = z.object({
  stageAdvanced: z.boolean(),
  booking: z.boolean(),
  recentReply: z.boolean(),
  openCommitment: z.boolean(),
  /** Human-readable evidence lines (Spanish). */
  details: z.array(z.string()),
  evaluatedAt: Iso,
});
export type ProspectProgressSignals = z.infer<typeof ProspectProgressSignalsSchema>;

export const ProspectBudgetReviewStatusSchema = z.enum(['paused_no_progress', 'review_pending', 'decided']);
export type ProspectBudgetReviewStatus = z.infer<typeof ProspectBudgetReviewStatusSchema>;
export const PROSPECT_BUDGET_REVIEW_STATUS_LABELS: Record<ProspectBudgetReviewStatus, string> = {
  paused_no_progress: 'Llamadas automáticas pausadas (sin avance)',
  review_pending: 'Revisión humana pendiente',
  decided: 'Decidido',
};

export const ProspectBudgetDecisionSchema = z.enum(['raise_cap_once', 'continue', 'stop']);
export type ProspectBudgetDecision = z.infer<typeof ProspectBudgetDecisionSchema>;
export const PROSPECT_BUDGET_DECISION_LABELS: Record<ProspectBudgetDecision, string> = {
  raise_cap_once: 'Aumentar el tope una vez',
  continue: 'Continuar sin tope para esta oportunidad',
  stop: 'Detener llamadas automáticas',
};

export const ProspectBudgetReviewSchema = z.object({
  id: Id,
  interestId: Id,
  contactId: Id,
  contactName: z.string().nullable(),
  projectId: Id,
  projectName: z.string(),
  status: ProspectBudgetReviewStatusSchema,
  decision: ProspectBudgetDecisionSchema.nullable(),
  reason: z.string(),
  signals: ProspectProgressSignalsSchema.nullable(),
  /** Estimated cost when the review was last evaluated. */
  estimatedCost: MoneySchema,
  uncostedEvents: z.number().int(),
  extraAllowance: MoneySchema.nullable(),
  raiseUsed: z.boolean(),
  taskId: Id.nullable(),
  decidedByName: z.string().nullable(),
  decidedAt: Iso.nullable(),
  decisionNote: z.string().nullable(),
  createdAt: Iso,
  updatedAt: Iso,
});
export type ProspectBudgetReview = z.infer<typeof ProspectBudgetReviewSchema>;

export const ProspectBudgetReviewQuerySchema = z.object({
  projectId: Id.optional(),
  /** open = paused or pending review (default); all = include decided. */
  status: z.enum(['open', 'all']).optional(),
});

export const DecideProspectBudgetSchema = z
  .object({
    decision: ProspectBudgetDecisionSchema,
    /** raise_cap_once only: extra centavos added to this opportunity's cap and alert. */
    raiseAmountMinor: MinorAmount.optional(),
    note: z.string().trim().min(3, 'Explica la decisión').max(1000),
  })
  .refine((b) => b.decision !== 'raise_cap_once' || (b.raiseAmountMinor !== undefined && BigInt(b.raiseAmountMinor) > 0n), { message: 'Indica cuánto aumentar el tope', path: ['raiseAmountMinor'] });
export type DecideProspectBudget = z.infer<typeof DecideProspectBudgetSchema>;

// ───────── Cost per prospect (lead view) ─────────

export const ProspectCostLineSchema = z.object({
  meter: ProspectCostMeterSchema,
  quantity: z.number(),
  events: z.number().int(),
  /** Known cost of this meter (null when no event of it has a cost). */
  cost: MoneySchema.nullable(),
  /** true when any part of the cost is our estimate (rate card or measured: false). */
  estimated: z.boolean(),
  uncostedEvents: z.number().int(),
});
export type ProspectCostLine = z.infer<typeof ProspectCostLineSchema>;

export const ProspectBudgetStateSchema = z.enum(['undefined', 'disabled', 'ok', 'near', 'reached']);
export type ProspectBudgetState = z.infer<typeof ProspectBudgetStateSchema>;
export const PROSPECT_BUDGET_STATE_LABELS: Record<ProspectBudgetState, string> = {
  undefined: 'Presupuesto sin definir',
  disabled: 'Presupuesto desactivado',
  ok: 'Dentro del presupuesto',
  near: 'Cerca del límite',
  reached: 'Límite alcanzado',
};

export const LeadAiCostSchema = z.object({
  interestId: Id,
  contactId: Id,
  projectId: Id,
  /** Sum of the known costs (estimated unless every line is measured). */
  total: MoneySchema,
  estimated: z.boolean(),
  /** Usage events with no cost (no provider cost and no rate): unknown, not zero. */
  uncostedEvents: z.number().int(),
  lines: z.array(ProspectCostLineSchema),
  budget: z.object({
    state: ProspectBudgetStateSchema,
    cap: MoneySchema.nullable(),
    alertAt: MoneySchema.nullable(),
    /** Cap + one-time human raise, when granted. */
    effectiveCap: MoneySchema.nullable(),
  }),
  review: ProspectBudgetReviewSchema.nullable(),
});
export type LeadAiCost = z.infer<typeof LeadAiCostSchema>;
